import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
// import { login } from '../api/auth' // Temporarily disabled: using static login bypass
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { setAuthEmail, setPendingMfaToken } from '@shared/libs/authStorage'
import { deriveChallenge, generateVerifier } from '@shared/libs/pkce'
import { useToast } from '@shared/toast/ToastProvider'
import { Button } from '@shared/ui/Button'
import { Input } from '@shared/ui/Input'
import { FormField } from '@shared/ui/FormField'
import { http } from '@shared/libs/http'
import { SessionSchema, type Session } from '@api/schemas/auth'

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8)
})

type FormValues = z.infer<typeof schema>

async function makeCodeChallenge() {
  const verifier = generateVerifier()
  return await deriveChallenge(verifier)
}

export function LoginPage() {
  const nav = useNavigate()
  const loc = useLocation()
  const from = (loc.state as any)?.from?.pathname ?? '/'
  const [error, setError] = useState<string | null>(null)
  const { register, handleSubmit, formState } = useForm<FormValues>({ resolver: zodResolver(schema) })
  const qc = useQueryClient()
  const { push } = useToast()

  // Prime CSRF cookie for login POST in dev
  // useEffect(() => {
  //   http.get('/auth/session').catch(() => {})
  // }, [])

  const onSubmit = async (values: FormValues) => {
    setError(null)
    // Static login bypass: admin@capexia.app / C@pexiA2025
    const ok = values.email === 'admin@capexia.app' && values.password === 'C@pexiA2025'
    if (!ok) {
      const msg = 'Invalid email or password'
      setError(msg)
      push({ type: 'error', title: 'Login failed', description: msg })
      return
    }
    // Create a static session and store in localStorage for useSession()
    const session: Session = {
      authenticated: true,
      needs_email_verify: false,
      needs_mfa: false,
      user: { id: '1', email: values.email },
      roles: [],
      permissions: []
    }
    localStorage.setItem('capexia.static_auth', JSON.stringify(session))
    // Update query cache and redirect
    qc.setQueryData(['session'], session)
    nav(from, { replace: true })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-gray-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Brand / Intro */}
        <div className="hidden md:flex flex-col justify-between p-10 bg-gradient-to-br from-teal-700 via-teal-600 to-teal-500 text-white">
          <div>
            <div className="mb-10">
              <div className="inline-flex items-center bg-white rounded-lg px-6 py-3 shadow-sm mx-auto">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68a9ceb78a3b2f5c335c3a70/3ab542b70_Capexia_logo.png"
                  alt="CAPEXIA"
                  className="h-12"
                />
              </div>
            </div>
            <h2 className="text-3xl font-bold leading-snug">Pilotage CAPEX, clair et sécurisé</h2>
            <p className="mt-3 text-teal-50/90">
              Centralisez vos projets d’investissement, suivez les budgets et accélérez vos validations.
            </p>
          </div>
          <ul className="space-y-2 text-sm text-teal-50/90">
            <li>• Workflow de validation</li>
            <li>• Budgets et dépenses en temps réel</li>
            <li>• Documents et traçabilité</li>
          </ul>
        </div>

        {/* Form */}
        <div className="p-8 md:p-10">
          <div className="mb-6 md:hidden flex items-center justify-center">
            <img
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68a9ceb78a3b2f5c335c3a70/3ab542b70_Capexia_logo.png"
              alt="CAPEXIA"
              className="h-8"
            />
          </div>
          <div className="mb-2">
            <h1 className="text-2xl font-semibold text-gray-900">Connexion</h1>
            <p className="text-sm text-gray-500">Accédez à votre espace CAPEXIA</p>
          </div>
          {error && <div className="mb-4 text-sm text-danger bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <form className="grid gap-4" onSubmit={handleSubmit(onSubmit)}>
            <FormField label="Email" message={formState.errors.email?.message}>
              <Input placeholder="admin@capexia.app" type="email" autoComplete="email" {...register('email')} />
            </FormField>
            <FormField label="Mot de passe" message={formState.errors.password?.message}>
              <Input placeholder="••••••••" type="password" autoComplete="current-password" {...register('password')} />
            </FormField>
            <div className="flex items-center justify-between text-sm">
              <Link className="text-teal-700 hover:text-teal-800" to="/forgot-password">Mot de passe oublié ?</Link>
            </div>
            <Button className="bg-teal-600 hover:bg-teal-700" disabled={formState.isSubmitting}>
              {formState.isSubmitting ? 'Connexion…' : 'Se connecter'}
            </Button>
          </form>
          <div className="mt-8 text-xs text-gray-400">
            En vous connectant, vous acceptez nos conditions d’utilisation et notre politique de confidentialité.
          </div>
        </div>
      </div>
    </div>
  )
}
 
